/**
 * Progress: mastery tracking and spaced review, stored locally.
 *
 * The scheduling and mastery logic here are pure functions so they can be tested
 * without a database. Persistence sits behind a tiny interface with two backends —
 * IndexedDB in the browser, memory everywhere else — so the app degrades to a
 * working session rather than crashing when storage is unavailable (private
 * browsing, a test runner, a locked-down profile).
 */

import { DB_VERSION as SHARED_DB_VERSION, STORES, openDb, request, transact } from '../storage/db';
import { announceWrite } from '../storage/writeLog';
import { LESSONS, LESSON_BY_ID } from './lessons';
import { emptyProgress, isMastered, type Lesson, type LessonProgress } from './types';

const STORE = STORES.progress;

/**
 * The database version now lives in `storage/db.ts`, because lesson progress
 * shares the `musicmentor` database with documents, settings and practice
 * history. Two modules opening the same database at different versions is how
 * you get a permanently blocked upgrade.
 *
 * Version 1 had no review scheduling; version 2 added `reviewCount` and `dueAt`;
 * version 3 added the local-first stores and did not touch this one.
 */
export const DB_VERSION = SHARED_DB_VERSION;

/**
 * Review intervals in days. Roughly doubling: something recalled correctly today
 * is worth re-checking tomorrow, then in three days, and so on.
 */
export const REVIEW_INTERVALS_DAYS = [1, 3, 7, 16, 35];
const DAY_MS = 24 * 60 * 60 * 1000;

// ------------------------------------------------------------------ pure logic

/** When a lesson mastered `reviewCount` times should next come back. */
export function nextDueAt(reviewCount: number, now: number): number {
  const index = Math.min(reviewCount, REVIEW_INTERVALS_DAYS.length - 1);
  return now + REVIEW_INTERVALS_DAYS[index] * DAY_MS;
}

export function isDue(progress: LessonProgress, now: number): boolean {
  return progress.dueAt !== null && progress.dueAt <= now;
}

/** Record an ear-training answer. A wrong answer resets the run, as it should. */
export function recordEar(progress: LessonProgress, correct: boolean, now: number): LessonProgress {
  const earStreak = correct ? progress.earStreak + 1 : 0;
  return {
    ...progress,
    earStreak,
    earBest: Math.max(progress.earBest, earStreak),
    lastPractisedAt: now,
  };
}

export function recordInk(progress: LessonProgress, passed: boolean, now: number): LessonProgress {
  return {
    ...progress,
    inkPassed: progress.inkPassed || passed,
    lastPractisedAt: now,
  };
}

export function recordCreative(progress: LessonProgress, now: number): LessonProgress {
  return { ...progress, creativeDone: true, lastPractisedAt: now };
}

/**
 * Called after any practice: if the lesson now meets its mastery rule, schedule
 * the next review. Re-mastering a lesson that had come due pushes it further out.
 */
export function scheduleIfMastered(
  lesson: Lesson,
  progress: LessonProgress,
  now: number,
): LessonProgress {
  if (!isMastered(lesson, progress)) return progress;
  const wasDue = progress.dueAt !== null && progress.dueAt <= now;
  const reviewCount = progress.dueAt === null ? 0 : wasDue ? progress.reviewCount + 1 : progress.reviewCount;
  return { ...progress, reviewCount, dueAt: nextDueAt(reviewCount, now) };
}

/** A lesson is available once every prerequisite has been mastered. */
export function isUnlocked(lesson: Lesson, all: Record<string, LessonProgress>): boolean {
  return lesson.prerequisites.every((id) => {
    const prerequisite = LESSON_BY_ID[id];
    const progress = all[id];
    return prerequisite && progress ? isMastered(prerequisite, progress) : false;
  });
}

/** Mastered lessons whose review has come due, soonest first. */
export function dueForReview(all: Record<string, LessonProgress>, now: number): Lesson[] {
  return LESSONS.filter((lesson) => {
    const progress = all[lesson.id];
    return progress ? isDue(progress, now) : false;
  }).sort((a, b) => (all[a.id].dueAt ?? 0) - (all[b.id].dueAt ?? 0));
}

/** The next lesson to work on: the first unlocked one that is not yet mastered. */
export function nextLesson(all: Record<string, LessonProgress>): Lesson | null {
  for (const lesson of LESSONS) {
    const progress = all[lesson.id] ?? emptyProgress(lesson.id);
    if (!isMastered(lesson, progress) && isUnlocked(lesson, all)) return lesson;
  }
  return null;
}

// ------------------------------------------------------------------ migrations

type StoredRecord = Partial<LessonProgress> & { lessonId: string };

/**
 * Bring a stored record up to the current shape. Kept separate from the database
 * so it can be tested directly, and so a future version only has to add a case.
 */
export function migrateRecord(record: StoredRecord): LessonProgress {
  return {
    ...emptyProgress(record.lessonId),
    ...record,
    // v1 -> v2: review scheduling did not exist.
    reviewCount: record.reviewCount ?? 0,
    dueAt: record.dueAt ?? null,
  };
}

// -------------------------------------------------------------------- storage

export interface ProgressBackend {
  load(): Promise<Record<string, LessonProgress>>;
  save(progress: LessonProgress): Promise<void>;
  clear(): Promise<void>;
}

/** Used in tests, and whenever IndexedDB is unavailable. */
export class MemoryBackend implements ProgressBackend {
  private data: Record<string, LessonProgress> = {};

  async load() {
    return { ...this.data };
  }
  async save(progress: LessonProgress) {
    this.data[progress.lessonId] = progress;
  }
  async clear() {
    this.data = {};
  }
}

/**
 * Reads and writes the `progress` store of the shared database.
 *
 * Opening is delegated to `storage/db.ts` so there is exactly one place that
 * knows the schema version. Records are still migrated on read, which is what
 * keeps the upgrade transaction short and `migrateRecord` directly testable.
 */
export class IndexedDbBackend implements ProgressBackend {
  async load(): Promise<Record<string, LessonProgress>> {
    const db = await openDb();
    const rows = await transact(db, STORE, 'readonly', (tx) =>
      request(tx.objectStore(STORE).getAll() as IDBRequest<StoredRecord[]>),
    );
    const out: Record<string, LessonProgress> = {};
    for (const raw of rows) out[raw.lessonId] = migrateRecord(raw);
    return out;
  }

  async save(progress: LessonProgress): Promise<void> {
    const db = await openDb();
    await transact(db, STORE, 'readwrite', (tx) => {
      tx.objectStore(STORE).put(progress);
    });
  }

  async clear(): Promise<void> {
    const db = await openDb();
    await transact(db, STORE, 'readwrite', (tx) => {
      tx.objectStore(STORE).clear();
    });
  }
}

/**
 * Announce a progress save so it can reach the outbox.
 *
 * Lesson progress is the one syncable record that does not go through a
 * repository, so without this wrapper an ordinary Learn save — the common case,
 * every finished task — was written locally and never queued. Progress made
 * after signing in stayed on one iPad indefinitely, and no sync test caught it
 * because they all enqueued by hand.
 *
 * The announcement happens *after* the local write resolves, and a pull's writes
 * are suppressed by `asRemoteApply`, so applying the server's own record cannot
 * echo back to it.
 */
class AnnouncingBackend implements ProgressBackend {
  constructor(private readonly inner: ProgressBackend) {}

  load() {
    return this.inner.load();
  }

  async save(progress: LessonProgress): Promise<void> {
    await this.inner.save(progress);
    announceWrite({ kind: 'lesson_progress', id: progress.lessonId });
  }

  clear() {
    return this.inner.clear();
  }
}

export function createBackend(): ProgressBackend {
  return new AnnouncingBackend(createRawBackend());
}

/** Without announcements. For migration tooling and tests that assert storage. */
export function createRawBackend(): ProgressBackend {
  try {
    if (typeof indexedDB !== 'undefined') return new IndexedDbBackend();
  } catch {
    // Blocked by the browser; fall through.
  }
  return new MemoryBackend();
}
